// Ejecutar una inspección (F3a): datos, iniciar, responder ítem por ítem (con observación y fotos por ítem),
// observaciones generales, hallazgos que se crearán, firma del inspector (K6: en pantalla con nombre, cédula, fecha y
// hora y hash, o papel firmado, o PDF con FirmaEC), PDF, cancelar y reabrir (solo admin, con motivo; queda en
// audit_logs). Firmada, queda bloqueada (trigger de M41) y la pantalla no deja editar.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad, { SignaturePadHandle } from '../../components/permits/SignaturePad';
import { logAudit } from '../../auditLog';
import { blobADataUrl } from '../../lib/permisos-evidencia';
import { sha256Hex } from '../../lib/psicosocial-codigos';
import { ACEPTA_EVIDENCIA, tamanoLegible } from '../../lib/pe/pe-evidencias';
import {
  CambioRespuesta, borrarArchivo, borrarInspeccion, cancelarInspeccion, MENSAJE_BORRAR_FIRMADA, cargarArchivos, cargarRespuestas, crearHallazgos, dataUrlArchivo, firmarInspeccion,
  FirmaParaSubir, guardarDatosInspeccion, guardarRespuesta, iniciarInspeccion, reabrirInspeccion, subirArchivo, urlArchivo,
} from '../../lib/inspecciones/insp-datos';
import {
  contenidoParaFirma, esFalla, fechaEc, fechaInspeccion, fotoObligatoria, faltantesParaFirmar, hallazgosAutomaticos, isoLocal, itemsDe,
  nombreArchivoPdf, textoRegla,
} from '../../lib/inspecciones/insp-logica';
import { FirmaPDF, generarInspeccionPDF } from '../../lib/inspecciones/insp-pdf';
import type { FilaArchivo, FilaHallazgo, FilaInspeccion, FilaRespuesta, ItemFoto, MetodoFirma, RolFirma, SedeMin, TrabajadorMin, ValorRespuesta } from '../../lib/inspecciones/insp-tipos';
import { ETIQUETA_METODO_FIRMA, ETIQUETA_RIESGO, ETIQUETA_ROL_FIRMA, ETIQUETA_VALOR } from '../../lib/inspecciones/insp-tipos';
import { Campo, ModalConfirmar } from '../pe/ComponentesPE';
import { ChipEstado } from './TablaInspecciones';
import { palabra, plural, veces } from '../../lib/plurales';

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

function descargarUrl(url: string, nuevaPestana = false) {
  const a = document.createElement('a'); a.href = url; a.rel = 'noopener noreferrer';
  if (nuevaPestana) a.target = '_blank';
  document.body.appendChild(a); a.click(); a.remove();
}

interface FirmanteForm { nombre: string; cedula: string; conTrazo: boolean; archivo: File | null }
const firmanteVacio = (): FirmanteForm => ({ nombre: '', cedula: '', conTrazo: false, archivo: null });
const ROLES: RolFirma[] = ['inspector', 'responsable'];
const activos = (f: Record<RolFirma, FirmanteForm>) => ROLES.filter(r => r === 'inspector' || f[r].nombre.trim() !== '');

const OPCIONES: Record<'cumple' | 'si_no', ValorRespuesta[]> = { cumple: ['cumple', 'no_cumple', 'no_aplica'], si_no: ['si', 'no'] };

export interface PropsEjecutar {
  inspeccion: FilaInspeccion;
  empresa: string;
  sedes: SedeMin[];
  varias: boolean;
  trabajadores: TrabajadorMin[];
  hallazgos: FilaHallazgo[];
  puedeEditar: boolean;
  puedeBorrar: boolean;
  usuarioId: string | null;
  onCambio: (i: FilaInspeccion | null, evento?: 'firmada' | 'cancelada') => void;
  onVolver: () => void;
  hoy?: Date;
}

export default function EjecutarInspeccion(p: PropsEjecutar) {
  const hoy = useMemo(() => p.hoy ?? new Date(), [p.hoy]);
  const [insp, setInsp] = useState(p.inspeccion);
  const [respuestas, setRespuestas] = useState<FilaRespuesta[]>([]);
  const [fotos, setFotos] = useState<FilaArchivo[]>([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [observaciones, setObservaciones] = useState(p.inspeccion.observations ?? '');
  const [firmar, setFirmar] = useState(false);
  const [metodo, setMetodo] = useState<MetodoFirma>('pantalla');
  const [firmantes, setFirmantes] = useState<Record<RolFirma, FirmanteForm>>({ inspector: firmanteVacio(), responsable: firmanteVacio() });
  const padInspector = useRef<SignaturePadHandle>(null); const padResponsable = useRef<SignaturePadHandle>(null);
  const pads: Record<RolFirma, React.RefObject<SignaturePadHandle | null>> = { inspector: padInspector, responsable: padResponsable };
  const [reabrir, setReabrir] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelar, setCancelar] = useState(false);
  const [borrar, setBorrar] = useState(false);
  const [avisoBorrar, setAvisoBorrar] = useState(false);
  // Hallazgos creados al firmar: si la firma falla después, el reintento no los vuelve a proponer.
  const [creados, setCreados] = useState<FilaHallazgo[]>([]);

  useEffect(() => { setInsp(p.inspeccion); setObservaciones(p.inspeccion.observations ?? ''); }, [p.inspeccion]);

  const cargar = useCallback(async () => {
    try {
      const [r, f] = await Promise.all([cargarRespuestas(insp.id), cargarArchivos(insp.company_id, { inspeccionId: insp.id })]);
      setRespuestas(r); setFotos(f);
    } catch (e) { setError(msg(e)); }
  }, [insp.id, insp.company_id]);
  useEffect(() => { cargar(); }, [cargar]);

  const firmada = !!insp.signed_at;
  const editable = p.puedeEditar && insp.status === 'en_curso' && !firmada;
  const sede = p.sedes.find(s => s.id === insp.site_id);
  const responsable = (insp.responsible_worker_id && p.trabajadores.find(t => t.id === insp.responsible_worker_id)?.full_name) || insp.responsible_role || '';
  const resp = useMemo(() => new Map(respuestas.map(r => [r.item_key, r])), [respuestas]);
  const fotosDe = useCallback((k: string) => fotos.filter(f => f.item_key === k), [fotos]);
  const propios = useMemo(() => [...p.hallazgos, ...creados.filter(c => !p.hallazgos.some(h => h.id === c.id))].filter(h => h.inspection_id === insp.id),
    [p.hallazgos, creados, insp.id]);
  const propuestas = useMemo(() => hallazgosAutomaticos(insp.template_snapshot, respuestas, propios, insp.performed_date ?? isoLocal(hoy)),
    [insp, respuestas, propios, hoy]);
  const firmantesLista = activos(firmantes).map(rol => ({ rol, nombre: firmantes[rol].nombre, cedula: firmantes[rol].cedula }));
  const faltan = [
    ...faltantesParaFirmar(insp, respuestas, fotos, firmantesLista, hoy).map(f => f.texto),
    ...(observaciones !== (insp.observations ?? '') ? ['Guarda las observaciones antes de firmar.'] : []),
    ...activos(firmantes).flatMap(rol => (metodo === 'pantalla' ? !firmantes[rol].conTrazo : !firmantes[rol].archivo)
      ? [metodo === 'pantalla' ? `Falta el trazo de la firma (${ETIQUETA_ROL_FIRMA[rol]}).` : `Falta el documento firmado (${ETIQUETA_ROL_FIRMA[rol]}).`] : []),
  ];

  async function ejecutar(accion: () => Promise<void>) {
    setOcupado(true); setError(''); setOk('');
    try { await accion(); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  const iniciar = () => ejecutar(async () => {
    const r = await iniciarInspeccion(insp, p.usuarioId, hoy);
    setInsp(r); p.onCambio(r); setOk('Inspección iniciada. Responde ítem por ítem.');
  });

  const responder = (it: ItemFoto, cambio: CambioRespuesta) => ejecutar(async () => {
    const r = await guardarRespuesta(insp, it.id, resp.get(it.id), cambio, p.usuarioId);
    setRespuestas(prev => [...prev.filter(x => x.item_key !== it.id), r]);
  });

  const subirFoto = (it: ItemFoto, archivo: File | null | undefined) => {
    if (!archivo) return;
    ejecutar(async () => {
      const f = await subirArchivo(insp.company_id, { kind: 'foto_item', inspeccionId: insp.id, itemKey: it.id }, archivo, p.usuarioId);
      setFotos(prev => [...prev, f]);
    });
  };
  const quitarFoto = (f: FilaArchivo) => ejecutar(async () => { await borrarArchivo(f); setFotos(prev => prev.filter(x => x.id !== f.id)); });
  const verArchivo = (ruta: string) => ejecutar(async () => descargarUrl(await urlArchivo(ruta), true));

  const guardarObservaciones = () => ejecutar(async () => {
    const r = await guardarDatosInspeccion(insp.id, { observations: observaciones.trim() || null }, p.usuarioId);
    setInsp(r); p.onCambio(r); setOk('Observaciones guardadas.');
  });

  async function datosPdf(firmas: FirmaPDF[], borrador: boolean, base: FilaInspeccion = insp) {
    const porItem: Record<string, (string | null)[]> = {};
    for (const f of fotos) if (f.item_key) porItem[f.item_key] = [...(porItem[f.item_key] ?? []), await dataUrlArchivo(f)];
    return {
      empresa: p.empresa, inspeccion: base, sede: sede?.name ?? '', cantonSede: sede?.canton_code ?? null, responsable, respuestas, fotos: porItem,
      hallazgos: propios, firmas, metodo: borrador ? null : metodo, borrador, fecha: new Date(),
    };
  }

  const descargarPdf = () => ejecutar(async () => {
    if (insp.pdf_path) { descargarUrl(await urlArchivo(insp.pdf_path, nombreArchivoPdf(insp))); return; }
    generarInspeccionPDF(await datosPdf([], true), hoy).save(nombreArchivoPdf(insp).replace('.pdf', '_borrador.pdf'));
  });

  const firmarUI = () => ejecutar(async () => {
    if (faltan.length) return;
    // 1) Hallazgos automáticos de los "no cumple" que lo indican (únicos por ítem en la base).
    const nuevos = await crearHallazgos(insp.company_id, propuestas.map(x => ({ ...x, site_id: insp.site_id, inspection_id: insp.id, origin: 'automatico' as const,
      responsible_worker_id: insp.responsible_worker_id, responsible_role: insp.responsible_role })), p.usuarioId);
    if (nuevos.length) setCreados(prev => [...prev, ...nuevos]);
    // 2) Firmas: hash SHA-256 del contenido + firmante; trazo PNG o archivo.
    const ahora = new Date().toISOString();
    const subir: FirmaParaSubir[] = []; const pdfFirmas: FirmaPDF[] = [];
    for (const rol of activos(firmantes)) {
      const base = { rol, nombre: firmantes[rol].nombre.trim(), cedula: firmantes[rol].cedula.trim(), firmado_en: ahora };
      const hash = await sha256Hex(contenidoParaFirma(insp, respuestas, base));
      const png = metodo === 'pantalla' ? await pads[rol].current?.toBlob() ?? null : null;
      if (metodo === 'pantalla' && !png) throw new Error(`Falta la firma de ${ETIQUETA_ROL_FIRMA[rol]}.`);
      subir.push({ rol, nombre: base.nombre, cedula: base.cedula, firmadoEn: ahora, hash, png, archivo: metodo === 'pantalla' ? null : firmantes[rol].archivo });
      pdfFirmas.push({ rol, nombre: base.nombre, cedula: base.cedula, firmadoEn: ahora, hash, imagen: png ? await blobADataUrl(png) : null, archivo: metodo !== 'pantalla' });
    }
    const hashInspeccion = await sha256Hex(subir.map(x => x.hash).join('|'));
    const datos = await datosPdf(pdfFirmas, false, { ...insp, status: 'completada' });
    datos.hallazgos = [...propios, ...nuevos];
    const pdf = generarInspeccionPDF(datos, hoy).output('blob');
    const r = await firmarInspeccion(insp, metodo, subir, pdf, hashInspeccion);
    logAudit({ company_id: insp.company_id, action: 'updated', entity_type: 'inspection', entity_id: insp.id, entity_name: `Inspección ${insp.template_name}`,
      details: { evento: 'inspeccion_firmada', metodo, firmantes: subir.map(x => x.rol), hash: hashInspeccion, hallazgos_creados: nuevos.length } });
    setInsp(r); setFirmar(false); setFirmantes({ inspector: firmanteVacio(), responsable: firmanteVacio() });
    p.onCambio(r, 'firmada');
    setOk(`Inspección firmada. Quedó bloqueada${nuevos.length ? ` y se ${palabra(nuevos.length, 'creó', 'crearon')} ${plural(nuevos.length, 'hallazgo', 'hallazgos')}` : ''}.`);
  });

  const reabrirUI = () => ejecutar(async () => {
    await reabrirInspeccion(insp.id, motivo);
    setReabrir(false); setMotivo('');
    const r = { ...insp, status: 'en_curso' as const, signed_at: null, sign_method: null, signatures: [], pdf_path: null, sign_hash: null };
    setInsp(r); p.onCambio(r);
    setOk('Inspección reabierta. El motivo quedó en la auditoría.');
  });

  const cancelarUI = () => ejecutar(async () => {
    await cancelarInspeccion(insp, p.usuarioId);
    setCancelar(false);
    const r = { ...insp, status: 'cancelada' as const };
    setInsp(r); p.onCambio(r, 'cancelada'); setOk('Inspección cancelada.');
  });

  const borrarUI = () => ejecutar(async () => {
    await borrarInspeccion(insp);
    setBorrar(false);
    logAudit({ company_id: insp.company_id, action: 'deleted', entity_type: 'inspection', entity_id: insp.id,
      entity_name: `Inspección ${insp.template_name} ${fechaEc(fechaInspeccion(insp))}` });
    p.onCambio(null);
    p.onVolver();
  });

  let n = 0;
  return (
    <div className="pe2-columna">
      <div className="pe2-cabecera">
        <button type="button" className="pe2-btn" onClick={p.onVolver}>← Volver a la lista</button>
        <div className="pe2-acciones">
          <button type="button" className="pe2-btn" onClick={descargarPdf} disabled={ocupado}>{insp.pdf_path ? 'Descargar PDF firmado' : 'Descargar borrador PDF'}</button>
        </div>
      </div>
      <section className="pe2-card" aria-label="Datos de la inspección">
        <div className="pe2-cabecera"><h3>{insp.template_name}</h3><ChipEstado estado={insp.status} /></div>
        <div className="pe2-res">
          {p.varias && <div><b>{sede?.name ?? '—'}</b><span>Sede</span></div>}
          <div><b>{insp.area || '—'}</b><span>Área</span></div>
          <div><b>{responsable || '—'}</b><span>Responsable</span></div>
          <div><b>{fechaEc(fechaInspeccion(insp))}</b><span>{insp.performed_date ? 'Realizada' : 'Programada'}</span></div>
          <div><b>v{insp.template_version}</b><span>Versión de la plantilla</span></div>
        </div>
        {firmada && (
          <div className="pe2-aviso" role="status">
            Inspección firmada el {new Date(insp.signed_at as string).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })}
            {insp.sign_method ? ` (${ETIQUETA_METODO_FIRMA[insp.sign_method]})` : ''}: queda bloqueada. Solo el administrador puede reabrirla con un motivo.
          </div>
        )}
        {insp.reopen_history.length > 0 && <p className="pe2-nota">Reabierta {veces(insp.reopen_history.length)}. Último motivo: {insp.reopen_history[insp.reopen_history.length - 1].motivo}</p>}
        <div className="pe2-acciones">
          {p.puedeEditar && insp.status === 'programada' && <button type="button" className="pe2-btn primario" onClick={iniciar} disabled={ocupado}>Iniciar inspección</button>}
          {p.puedeEditar && !firmada && (insp.status === 'programada' || insp.status === 'en_curso') && (
            <button type="button" className="pe2-btn peligro" onClick={() => setCancelar(true)} disabled={ocupado}>Cancelar inspección</button>
          )}
          {firmada && p.puedeBorrar && <button type="button" className="pe2-btn" onClick={() => setReabrir(true)} disabled={ocupado}>Reabrir</button>}
          {/* F3a2: borrar (solo admin). Firmada no se borra: el botón explica que primero hay que reabrirla. */}
          {p.puedeBorrar && (
            <button type="button" className="pe2-btn peligro" disabled={ocupado} aria-describedby={avisoBorrar ? 'insp-aviso-borrar' : undefined}
              onClick={() => (firmada ? setAvisoBorrar(true) : setBorrar(true))}>Borrar</button>
          )}
        </div>
        {avisoBorrar && firmada && <p id="insp-aviso-borrar" className="pe2-aviso" role="status">{MENSAJE_BORRAR_FIRMADA}</p>}
        {reabrir && (
          <div className="pe2-sub-card pe2-card" role="region" aria-label="Reabrir la inspección">
            <Campo etiqueta="Motivo para reabrir (queda en la auditoría)">
              <textarea value={motivo} onChange={e => setMotivo(e.target.value)} disabled={ocupado} />
            </Campo>
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" onClick={() => setReabrir(false)} disabled={ocupado}>Cancelar</button>
              <button type="button" className="pe2-btn primario" onClick={reabrirUI} disabled={ocupado || motivo.trim().length < 10}>Reabrir inspección</button>
            </div>
          </div>
        )}
      </section>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {ok && <p className="pe2-ok" role="status">{ok}</p>}
      {insp.status === 'programada' && <p className="pe2-nota">Inicia la inspección para responderla. Al iniciarla se toma la versión vigente de la plantilla.</p>}

      {insp.template_snapshot.sections.map(s => (
        <section key={s.id} className="pe2-card" aria-label={s.title}>
          <h3>{s.title}</h3>
          {s.items.map(it => {
            n++;
            const r = resp.get(it.id);
            const fs = fotosDe(it.id);
            const regla = textoRegla(it.rule_key, sede?.canton_code ?? null);
            const fotoReq = fotoObligatoria(it, r) && fs.length === 0;
            return (
              <div key={it.id} className={`insp-item${esFalla(it, r) ? ' falla' : ''}`} role="group" aria-label={`Ítem ${n}: ${it.text}`}>
                <h4>{n}. {it.text}{it.required ? '' : ' (opcional)'}</h4>
                {it.help && <span className="insp-ayuda">{it.help}</span>}
                {regla && <span className="insp-ayuda">{regla}</span>}
                {it.reference && <span className="insp-ayuda">Referencia: {it.reference}</span>}
                {(it.response_type === 'cumple' || it.response_type === 'si_no') && (
                  <div className="insp-opciones" role="group" aria-label={`Respuesta del ítem ${n}`}>
                    {OPCIONES[it.response_type].map(v => (
                      <button key={v} type="button" aria-pressed={r?.value === v} className={v === 'no_cumple' || (it.response_type === 'si_no' && v === it.fail_value) ? 'no' : undefined}
                        disabled={!editable || ocupado} onClick={() => responder(it, { value: r?.value === v ? null : v })}>{ETIQUETA_VALOR[v]}</button>
                    ))}
                  </div>
                )}
                {it.response_type === 'numero' && (
                  <Campo etiqueta={`Valor del ítem ${n}${it.unit ? ` (${it.unit})` : ''}`}>
                    <input type="text" inputMode="decimal" key={`${it.id}-${r?.number_value ?? ''}`} defaultValue={r?.number_value === null || r?.number_value === undefined ? '' : String(r.number_value).replace('.', ',')}
                      disabled={!editable || ocupado} onBlur={e => {
                        const t = e.target.value.trim().replace(',', '.');
                        const v = t === '' ? null : Number(t);
                        if (v !== null && !Number.isFinite(v)) { setError(`El valor del ítem ${n} no es un número.`); return; }
                        if (v !== (r?.number_value ?? null)) responder(it, { number_value: v });
                      }} />
                  </Campo>
                )}
                {it.response_type === 'texto' && (
                  <Campo etiqueta={`Respuesta del ítem ${n}`}>
                    <textarea key={`${it.id}-${r?.text_value ?? ''}`} defaultValue={r?.text_value ?? ''} disabled={!editable || ocupado}
                      onBlur={e => { const v = e.target.value.trim() || null; if (v !== (r?.text_value ?? null)) responder(it, { text_value: v }); }} />
                  </Campo>
                )}
                {it.response_type !== 'texto' && (
                  <Campo etiqueta={`Observación del ítem ${n}`}>
                    <input type="text" key={`${it.id}-n-${r?.note ?? ''}`} defaultValue={r?.note ?? ''} disabled={!editable || ocupado}
                      onBlur={e => { const v = e.target.value.trim() || null; if (v !== (r?.note ?? null)) responder(it, { note: v }); }} />
                  </Campo>
                )}
                <div className="insp-fotos">
                  {fs.length > 0 && (
                    <ul aria-label={`Fotos del ítem ${n}`}>
                      {fs.map(f => (
                        <li key={f.id} className="pe2-evidencia-botones">
                          <button type="button" className="pe2-btn" onClick={() => verArchivo(f.storage_path)}>Ver {f.file_name} ({tamanoLegible(f.size_bytes)})</button>
                          {editable && <button type="button" className="pe2-btn peligro" aria-label={`Quitar ${f.file_name}`} onClick={() => quitarFoto(f)} disabled={ocupado}>Quitar</button>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {editable && (
                    <label className="pe2-btn pe2-btn-archivo">
                      Agregar foto
                      <input type="file" accept={ACEPTA_EVIDENCIA} capture="environment" aria-label={`Foto del ítem ${n}`} disabled={ocupado}
                        onChange={e => { subirFoto(it, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  )}
                  {fotoReq && <span className="insp-obligatoria">Foto obligatoria{it.response_type === 'foto' ? '' : ' en este "no cumple"'}.</span>}
                </div>
              </div>
            );
          })}
        </section>
      ))}
      {itemsDe(insp.template_snapshot).length === 0 && <p className="pe2-nota">La plantilla no tiene ítems.</p>}

      <section className="pe2-card" aria-label="Observaciones generales">
        <Campo etiqueta="Observaciones generales">
          <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)} disabled={!editable || ocupado} />
        </Campo>
        {editable && <div className="pe2-acciones"><button type="button" className="pe2-btn" onClick={guardarObservaciones} disabled={ocupado || observaciones === (insp.observations ?? '')}>Guardar observaciones</button></div>}
      </section>

      <section className="pe2-card" aria-label="Hallazgos de la inspección">
        <h3>Hallazgos</h3>
        {propios.length === 0 && propuestas.length === 0 && <p className="pe2-nota">Sin hallazgos.</p>}
        {propios.map(h => <p key={h.id} className="pe2-nota">• {h.description} ({ETIQUETA_RIESGO[h.risk_level]}) — se sigue en la pestaña Hallazgos.</p>)}
        {!firmada && propuestas.length > 0 && (
          <>
            <p className="pe2-nota">Al firmar se crearán estos hallazgos (de los "no cumple" que lo indican):</p>
            <ul className="pe2-vista-lista">{propuestas.map(x => <li key={x.item_key}>{x.description} · riesgo {ETIQUETA_RIESGO[x.risk_level].toLowerCase()} · fecha límite sugerida {fechaEc(x.due_date)}</li>)}</ul>
          </>
        )}
      </section>

      {editable && (
        <section className="pe2-card" aria-label="Firmar la inspección">
          <div className="pe2-cabecera"><h3>Firma del inspector</h3>
            {!firmar && <button type="button" className="pe2-btn primario" onClick={() => setFirmar(true)}>Firmar inspección</button>}
          </div>
          {firmar && (
            <>
              <Campo etiqueta="Cómo se firma">
                <select value={metodo} disabled={ocupado} onChange={e => setMetodo(e.target.value as MetodoFirma)}>
                  {(['pantalla', 'papel', 'firmaec'] as MetodoFirma[]).map(m => <option key={m} value={m}>{ETIQUETA_METODO_FIRMA[m]}</option>)}
                </select>
              </Campo>
              {ROLES.map(rol => (
                <div key={rol} className="pe2-card pe2-sub-card">
                  <h4>{ETIQUETA_ROL_FIRMA[rol]}{rol === 'responsable' ? ' (opcional)' : ''}</h4>
                  <Campo etiqueta={`Nombre (${ETIQUETA_ROL_FIRMA[rol]})`}>
                    <input type="text" value={firmantes[rol].nombre} disabled={ocupado} onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], nombre: e.target.value } })} />
                  </Campo>
                  <Campo etiqueta={`Cédula (${ETIQUETA_ROL_FIRMA[rol]})`}>
                    <input type="text" inputMode="numeric" maxLength={10} value={firmantes[rol].cedula} disabled={ocupado}
                      onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], cedula: e.target.value.replace(/\D/g, '') } })} />
                  </Campo>
                  {metodo === 'pantalla' ? (
                    <SignaturePad ref={pads[rol]} disabled={ocupado} onChange={vacio => setFirmantes(f => ({ ...f, [rol]: { ...f[rol], conTrazo: !vacio } }))} />
                  ) : (
                    <Campo etiqueta={metodo === 'papel' ? 'Papel firmado (foto o PDF escaneado)' : 'PDF firmado con FirmaEC'}>
                      <input type="file" accept={metodo === 'papel' ? ACEPTA_EVIDENCIA : '.pdf,application/pdf'} disabled={ocupado} aria-label={`Archivo firmado (${ETIQUETA_ROL_FIRMA[rol]})`}
                        onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], archivo: e.target.files?.[0] ?? null } })} />
                    </Campo>
                  )}
                </div>
              ))}
              {faltan.length > 0 && <div className="pe2-aviso" role="status"><b>Para firmar falta:</b><ul className="pe2-vista-lista">{faltan.map(f => <li key={f}>{f}</li>)}</ul></div>}
              <div className="pe2-acciones">
                <button type="button" className="pe2-btn" onClick={() => setFirmar(false)} disabled={ocupado}>Cancelar</button>
                <button type="button" className="pe2-btn primario" onClick={firmarUI} disabled={ocupado || faltan.length > 0}>{ocupado ? 'Firmando…' : 'Firmar y bloquear'}</button>
              </div>
            </>
          )}
        </section>
      )}
      <ModalConfirmar abierto={cancelar} titulo="Cancelar la inspección" textoConfirmar="Cancelar inspección" ocupado={ocupado}
        mensaje="La inspección queda cancelada (no se borra) y su evento del Calendario SSO también. Si es de una programación, se genera la siguiente."
        onConfirmar={cancelarUI} onCancelar={() => setCancelar(false)} />
      <ModalConfirmar abierto={borrar} titulo="Borrar la inspección" textoConfirmar="Borrar" ocupado={ocupado}
        mensaje="Se borra la inspección con sus respuestas, fotos y hallazgos, y su evento del Calendario SSO. No se puede deshacer."
        onConfirmar={borrarUI} onCancelar={() => setBorrar(false)} />
    </div>
  );
}
